import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";

import ErrorBanner from "../../components/ErrorBanner";
import { useQueryGetClientsMasterList } from "../../data/services/clientAdminService";
import { useMutatePostReportExclusionClients } from "../../data/services/reportExclusionService";

// Adapted from FeatureAccessAddClientsModal, minus the feature key: an
// exclusion is one client for one season.
const ReportExclusionsAddClientsModal = ({ oePeriodId, onClose, onSubmit }) => {
  const [submitted, setSubmitted] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [clientsDataSource, setClientsDataSource] = React.useState([]);
  const [selectedValues, setSelectedValues] = React.useState([]);

  const { isLoading: availableClientsLoading, data: availableClients } =
    useQueryGetClientsMasterList();
  const addClientsHandler = useMutatePostReportExclusionClients();

  React.useEffect(() => {
    if (availableClientsLoading || !availableClients?.data) {
      return;
    }

    // No "All Clients" option here, unlike the feature access modal: excluding
    // every client would empty the reports outright, which is never the intent.
    const mapped = availableClients.data.map((client) => ({
      ...client,
      clientDispName: `${client.clientId} - ${client.clientName}`,
    }));

    setClients(mapped);
    setClientsDataSource(mapped.slice());
  }, [availableClientsLoading, availableClients]);

  const handleSubmit = async () => {
    const clientIds = selectedValues.map((item) => item.clientId);

    if (clientIds.length === 0) {
      return;
    }

    setSubmitted(true);

    try {
      await addClientsHandler.mutateAsync({ oePeriodId, clientIds });
      setSubmitted(false);
      onSubmit();
    } catch {
      setSubmitted(false);
    }
  };

  const itemRender = (li, itemProps) => {
    const itemChildren = (
      <span>
        <input
          type="checkbox"
          name={itemProps.dataItem}
          checked={itemProps.selected}
          onChange={(e) => itemProps.onClick(itemProps.index, e)}
        />
        &nbsp;{li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const filterChange = (event) => {
    setClientsDataSource(filterBy(clients.slice(), event.filter));
  };

  return (
    <Dialog title={"Exclude clients from reports"} onClose={onClose} width={600}>
      <ErrorBanner
        handler={addClientsHandler}
        onError={() => setSubmitted(false)}
      />
      <p>
        Select the clients to leave out of the dashboard charts and the status
        tracking report for this OE period. Clients already excluded are left
        unchanged.
      </p>
      <MultiSelect
        data={clientsDataSource}
        itemRender={itemRender}
        textField={"clientDispName"}
        dataItemKey={"clientId"}
        autoClose={false}
        value={selectedValues}
        onChange={(event) => setSelectedValues(event.value)}
        filterable={true}
        onFilterChange={filterChange}
        tags={
          selectedValues.length > 0
            ? [
                {
                  text: `${selectedValues.length} clients selected`,
                  data: [...selectedValues],
                },
              ]
            : []
        }
      />
      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          type={"submit"}
          themeColor={"success"}
          disabled={submitted || selectedValues.length === 0}
          onClick={handleSubmit}
        >
          {"Exclude Clients"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default ReportExclusionsAddClientsModal;
